import MicroModal from 'micromodal'; // https://micromodal.vercel.app/

/* Language Alert
----------------------------------------------------------------------------- */

// Show alert if page language differs from browser and a matching link exists

// Debug Query Parameters:
/*
?otLangAttr=es-ES|ca-FR|xx-XX|etc:  apply value to html element lang attribute to emulate translated page
?otLangDebug=true:                  show debugging information in the console
?otLangLocalStorage=false           prevent writing to localStorage
?otLangSwitcherNavigate=false:      prevent navigation on language switcher link click
?otLangTest=true:                   run test suite
*/

const otLanguageAlert = {
    DELAY: 1000,
    LANG_ALERT_ID: 'ot-lang-alert', // Excluded "#" so MicroModal can reference
    LANG_SELECTOR_CLASS: '.smartling-language-selector',
    LOCAL_STORAGE_KEY: 'otLangAlertDismissed',
    debug: false,
    test: false,
    urlParams: new URLSearchParams(document.location.search),
    alertText: {
        'default': {
            body: "It looks like you are visiting from another region. Would you like to switch to [language_link]?",
            link: 'View our full list of available regions'
        },
    },
    async init() {
        this.debug = this.urlParams.get('otLangDebug') === 'true';
        this.test = this.urlParams.get('otLangTest') === 'true';
        const langAttr = this.urlParams.get('otLangAttr');

        const $langAlert = document.querySelector(`#${this.LANG_ALERT_ID}`);
        const $langSelector = document.querySelector(`${this.LANG_SELECTOR_CLASS}`);

        if (langAttr) document.documentElement.lang = langAttr;

        if (!$langAlert) {
            console.warn(`#${this.LANG_ALERT_ID} not found in DOM`);
            return;
        };

        if (!$langSelector) {
            console.warn(`${this.LANG_SELECTOR_CLASS} not found in DOM`);
            return;
        };

        this.interceptSwitcherClicks($langSelector);

        const previouslyDismissed = !!localStorage.getItem(this.LOCAL_STORAGE_KEY);
        const pageLang = document.documentElement.lang ? document.documentElement.lang.trim() : '';
        const normalizedPageLang = pageLang.toLocaleLowerCase();

        const browserLang = (
            Array.isArray(navigator.languages)
                ? navigator.languages[0]
                : navigator.language
            ) || '';
        const normalizedBrowserLang = browserLang.toLocaleLowerCase();

        // Do a comparison here to determine if getting available langs and running is even necessary, exit early if match
        if (!this.test && (normalizedBrowserLang === normalizedPageLang)) {
            if (this.debug) console.log(`Browser lang (${normalizedBrowserLang}) matches page lang (${normalizedPageLang})`);
            return;
        }

        const availableLangs = this.getAvailableLangs();

        if (this.debug) {
            console.table([
                ["Alert Previously Dismissed", previouslyDismissed],
                ["Page Lang", normalizedPageLang],
                ["Browser Lang", normalizedBrowserLang],
            ]);
        }

        if (!availableLangs) {
            if (this.debug) console.log('Available langs not found')
            return;
        } else {
            if (this.debug) console.log("Available langs: ", availableLangs);
        }

        if (this.test) {
            this.runTests(availableLangs, $langAlert, $langSelector)
        } else {
            if (previouslyDismissed || !normalizedPageLang || !normalizedBrowserLang) {
                if (this.debug) console.log('Exited before running due to one of the following values:', {previouslyDismissed, normalizedPageLang, normalizedBrowserLang})
                return;
            }

            this.run({ pageLang: normalizedPageLang, browserLang: normalizedBrowserLang, availableLangs, $langAlert, $langSelector });
        }
    },
    run({ pageLang, browserLang, availableLangs, test, $langAlert }) {
        const dBObject = this.getContentFromDB();

        if (!dBObject) return;

        const suggestedLang = this.getSuggestedLang({
            pageLang,
            browserLang,
            dBObject,
            availableLangs: availableLangs.map(item => item?.locale)
        });

        const suggestedLanguageObject = suggestedLang && availableLangs.find(lang => lang.locale === suggestedLang);

        if (!suggestedLanguageObject) return;

        if (this.debug) console.log("Suggested Language: ", suggestedLanguageObject);

        if (test) return suggestedLanguageObject; // Don't trigger modal if running test suite

        this.populateModal({...suggestedLanguageObject}, dBObject, $langAlert);

        setTimeout(() => {
            MicroModal.show(
                this.LANG_ALERT_ID, {
                // onShow: modal => console.info(`${modal.id} is shown`),
                onClose: modal => this.closeModal(modal),
                closeTrigger: 'data-ot-lang-alert-close',
                disableScroll: true,
                disableFocus: true,
                awaitOpenAnimation: true,
                awaitCloseAnimation: true,
                debugMode: false
            });
        }, this.DELAY);
    },
    runTests(availableLangs, $langAlert, $langSelector) {
        const script = document.createElement('script');
        script.src = 'wp-content/plugins/ot-language-alert/tests/language-alert.test.js';
        script.onload = () => {
            const testSuite = window.otLanguageAlertTestSuite;

            if (testSuite && typeof testSuite.runTests === 'function') {
                testSuite.runTests({
                    availableLangs,
                    $langAlert,
                    $langSelector
                });
            }
        };

        script.onerror = () => {
            console.warn('Test script failed to load:', script.src);
        };

        document.head.appendChild(script);
    },
    addModalClickEvents($langAlert, $switcher, $switcherTriggerBtn ) {
        $langAlert?.addEventListener('click', (e) => {
            e.preventDefault();

            if (e.target.hasAttribute('data-ot-lang-alert-suggested')) {
                // Click suggested language link
                if (this.debug) console.log(`Clicked suggested language`);
                this.setLocalStorage();
                if (this?.urlParams.get('otLangSwitcherNavigate') === 'false') return;
                window.location.href = e.target.href;
            } else if (e.target === $switcherTriggerBtn) {
                // Click switcher trigger button
                if (this.debug) console.log(`Clicked switcher trigger - scroll to switcher and open it)`);
                e.target.blur();
                
                setTimeout(() => {
                    const reducedMotion = 
                        window.matchMedia(`(prefers-reduced-motion: reduce)`) === true || 
                        window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;

                    $switcher?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
                    MicroModal.close(this.LANG_ALERT_ID);
                    $switcher?.click();
                })
            }
        });
    },
    closeModal(modal) {
        if (this.debug) console.log(`Alert dismissed`);
        // Prevent aria-hidden console warning
        document.querySelector('.lang-modal__close').blur();
        document.querySelector('.lang-modal__overlay').blur();

        this.setLocalStorage();
    },
    getAvailableLangs() {
        const $langs = document.querySelectorAll('link[hreflang]');

        return [...$langs]?.map($lang => {
            const url = $lang?.href;
            const locale = $lang.hreflang;
            // $lang lacks label - need to reference switcher instead (find by matching url), default to language code if label not found
            const label = this.getLabelFromSwitcherLink(url) || $lang?.hreflang;

            if (url && locale && label) {
                return { locale, url, label };
            }
        });
    },
    getContentFromDB() {
        // Retrieve language alert content from localized variable
        if (typeof window.OTLanguageAlertContent !== 'undefined') {
            return window.OTLanguageAlertContent;
        }

        return [];
    },
    getLabelFromSwitcherLink(url) {
        const $langSelector = document.querySelector(`${this.LANG_SELECTOR_CLASS}`);
        const $langLinks = $langSelector?.querySelectorAll(`[href], [data-lang]`);
        const urlPath = (() => { try { return new URL(url).pathname; } catch(e) { return url; } })();
        // Extract lang code from url path e.g. /de/ → de, returns 'en' for English (no prefix)
        const langFromUrl = (() => {
            const match = urlPath.match(/^\/([a-z]{2})(\/|$)/);
            return match ? match[1] : 'en';
        })();
        const $langLink = Array.from($langLinks).find($link => {
            const href = $link.getAttribute('href');
            const dataLang = $link.getAttribute('data-lang');
            return (href && (href === url || href.includes(url) || href.includes(urlPath)))
                || (dataLang && langFromUrl && dataLang === langFromUrl);
        });

        return $langLink?.querySelector('.smartling-lang-text')?.innerText || '';
    },
    getSuggestedLang({ pageLang, browserLang, dBObject, availableLangs, exactMatch = false}) {
        /**
         * Determines whether to show a language redirect alert based on browser preferences
         * and available translated pages. Supports multiple matching strategies.
         * 
         * @param {Object} options - Configuration object
         * @param {string} options.pageLang - The current page's lang attribute (e.g., 'en-us', 'es-es')
         * @param {string} options.browserLang - Browser's preferred language as per user preferences
         * @param {Object[]} options.dBObject - Array of language data objects from the database, each with locale, default_locale, body, and link properties
         * @param {string[]} options.availableLangs - Array of available page translation locale codes for the current page
         * @param {boolean} [options.exactMatch=false] - When true, only suggest a language if an exact match is found in availableLangs; skips base lang comparison
         * @returns {string|undefined|null} - The suggested locale code (e.g., 'en-ca'), undefined if no redirect needed, or null if no match found
         *
         * Matching strategy:
         * 1. Normalize browser and page langs (lowercase, strip 3rd segment e.g. 'en-gb-oxendict' → 'en-gb')
         * 2. If browser lang matches page lang exactly: exit
         * 3. Check if browser and page share a base lang:
         *    a. If browser is a bare base lang (e.g. 'en', 'es'): already on a matching page - exit
         *    b. If browser is the default locale for its base (e.g. 'en-us', 'es-es') and page is the bare base: already on default - exit
         *    c. Otherwise: fall through to check available langs
         * 4. Look for exact browser lang match in available langs → suggest it
         * 5. If exactMatch flag is set, or base match was found but no exact match: exit
         * 6. Compare browser base lang against available lang bases:
         *    - If configured default_locale for that base is available → suggest it
         *    - Else if bare base is available → suggest it
         *    - Else suggest first available lang that shares the base
         * 7. No match found: exit
         */

        // Step 1: Normalize — strip 3rd segment and lowercase
        // Already lowercased in init(), but repeated here for the test suite
        const normalizedBrowserLang = browserLang.replace(/^((?:[^-]*-){1}[^-]*).*/, '$1').toLocaleLowerCase();
        const normalizedPageLang = pageLang.toLocaleLowerCase();

        if (this.debug) console.log(`Normalized browser lang: ${normalizedBrowserLang}, normalized page lang: ${normalizedPageLang}`);

        const browserLangBase = normalizedBrowserLang.split('-')[0];
        const pageLangBase = normalizedPageLang.split('-')[0];
        const baseMatch = browserLangBase === pageLangBase ? browserLangBase : false;

        // Build map of base lang → default locale (e.g. { 'en': 'en-us', 'fr': 'fr-fr' })
        // Derived from dBObject entries that have a default_locale configured
        const defaultLocales = dBObject.reduce((acc, entry) => {
            if (entry.default_locale) {
                acc[entry.locale.toLowerCase()] = entry.default_locale.toLowerCase();
            }
            return acc;
        }, {});

        // If browser lang is the default locale for its base, compare against the bare base instead
        // (e.g. en-us → en, es-es → es) so it matches bare base entries in availableLangs
        // Only do this if the bare base is actually available — if not, keep the full locale
        // so it can still match regional entries directly (e.g. zh-cn when only zh-CN and zh-TW are available)
        const defaultLocale = defaultLocales[browserLangBase];
        const compareLang = defaultLocale && normalizedBrowserLang === defaultLocale && availableLangs.includes(browserLangBase)
            ? browserLangBase
            : normalizedBrowserLang;

        // Step 2: Exact match between browser and page — no redirect needed
        if (normalizedBrowserLang === normalizedPageLang) {
            if (this.debug) console.log(`Browser lang (${normalizedBrowserLang}) matches page lang (${normalizedPageLang}) - exit`);
            return undefined;
        }

        // Step 3: Base lang match
        if (baseMatch) {
            // 3a: Browser is bare base lang (e.g. 'en') — any page with that base is acceptable
            if (normalizedBrowserLang === browserLangBase) {
                if (this.debug) console.log(`Browser is bare '${browserLangBase}', page is also '${browserLangBase}' base - exit`);
                return undefined;
            }

            // 3b: Browser is the default locale for its base (e.g. 'en-us') and page is the bare base (e.g. 'en') — already on default
            if (defaultLocale && normalizedBrowserLang === defaultLocale && normalizedPageLang === browserLangBase) {
                if (this.debug) console.log(`Browser is default locale '${normalizedBrowserLang}' for base '${browserLangBase}', page is bare base - exit`);
                return undefined;
            }

            // 3c: Bases match but not an exit case — look for a more specific match in availableLangs
            if (this.debug) console.log(`Browser lang (${normalizedBrowserLang}) and page (${normalizedPageLang}) share '${browserLangBase}' base - look for exact match in available langs`);
        } else {
            if (this.debug) console.log(`Browser lang base (${browserLangBase}) does not match page lang base (${pageLangBase}) - look for exact match in available langs`);
        }

        // Step 4: Look for exact match in availableLangs
        for (const availableLang of availableLangs) {
            if (compareLang === availableLang) {
                if (this.debug) console.log(`Browser lang (${compareLang}) found in available langs (${availableLang}) - show alert`);
                return availableLang;
            }
        }

        // Step 5: No exact match found
        if (exactMatch) {
            if (this.debug) console.log(`exactMatch flag is true - do not compare bases - exit`);
            return undefined;
        } else if (baseMatch) {
            // Bases matched but no exact match in availableLangs — don't broaden search further
            if (this.debug) console.log(`Browser lang (${normalizedBrowserLang}) not found in available languages (while bases match) - exit`);
            return undefined;
        } else {
            if (this.debug) console.log(`Browser lang (${normalizedBrowserLang}) not found in available languages - compare lang bases`);
        }

        // Step 6: Compare browser base lang against available lang bases
        for (const availableLang of availableLangs) {
            const availableLangBase = availableLang.split('-')[0];

            if (browserLangBase === availableLangBase) {
                // Find the dBObject entry for the bare base to check for a configured default locale
                const defaultEntry = dBObject.find(entry =>
                    entry.default_locale && entry.locale.toLowerCase() === browserLangBase
                );
                // Prefer: configured default (if available) → bare base (if available) → first base match
                const defaultSuggestion = defaultEntry && availableLangs.includes(defaultEntry.default_locale.toLowerCase())
                    ? defaultEntry.default_locale.toLowerCase()
                    : null;
                const suggestion = defaultSuggestion || (availableLangs.includes(browserLangBase) ? browserLangBase : availableLang);
                if (this.debug) console.log(`Browser lang base (${browserLangBase}) found in available languages - show alert (${suggestion})`);
                return suggestion;
            }
        }

        // Step 7: No match found
        if (this.debug) console.log(`Browser lang base (${browserLangBase}) not found in available languages - exit`);
        return null;
    },
    interceptSwitcherClicks($langSelector) {
        // Write to local storage on manual language switch
        const $langLinks = $langSelector?.querySelectorAll(`[href]`);

        $langLinks?.forEach($langLink => {
            $langLink.classList.add('ga4-lang-modal-link');

            $langLink.addEventListener('click', (e) => {
                e.preventDefault();
                if (this.debug) console.log(`Intercept language switcher click`);
                this.setLocalStorage();
                if (this.debug) console.log(`Proceed to clicked language link: ${$langLink.href}`);
                if (this?.urlParams.get('otLangSwitcherNavigate') === 'false') return;
                window.location.href = $langLink.href;
            });
        });
    },
    populateModal(suggestedLang, dBObject, $langAlert) {
        const formatAlertText = (lang) => {
            // Replace link/href placeholders in alertText with language.href if not specified in alertText
            const template = dBObject?.find(data => data.locale.toLowerCase() === lang.locale.toLowerCase()) || this.alertText.default;
            const alertText = { ...template };

            // sl_norewrite class prevents smartling from injecting the current page's url's language code into the href
            alertText.body = alertText.body.replace(
                '[language_link]',
                '<a href="' + lang.url + '" class="sl_norewrite ga4-lang-alert-suggested-link" data-ot-lang-alert-suggested>' + lang.label + '</a>'
            );

            return alertText;
        }

        const alertText = formatAlertText(suggestedLang);

        // Body Text
        const $alertBody = document.createElement('p');
        $alertBody.innerHTML = alertText.body;

        // Modal Content
        const $langAlertContent = $langAlert.querySelector('[data-ot-lang-alert-content]');
        $langAlertContent.append($alertBody);

        // Close Button Text
        if (alertText.close_text) {
            const $closeBtn = $langAlert.querySelector('button[data-ot-lang-alert-close]'); // Specify button so it doesn't apply to bg overlay
            $closeBtn.innerText = alertText.close_text;
        }

        // Lang Switcher Trigger Button
        const $switcher = document.querySelector('.smartling-language-toggle');

        if (!$switcher) return;

        const $switcherTriggerBtn = document.createElement('a');
        $switcherTriggerBtn.setAttribute('role', 'button');
        $switcherTriggerBtn.setAttribute('href', '#lang-switcher');
        $switcherTriggerBtn.classList.add('lang-modal-switcher-trigger-btn', 'ga4-lang-alert-modal-trigger');
        $switcherTriggerBtn.innerText = alertText.link;

        this.addModalClickEvents($langAlert, $switcher, $switcherTriggerBtn);

        $langAlertContent.append($switcherTriggerBtn);
    },
    setLocalStorage() {
        if (this.debug) console.log(`Write to localStorage (if otLangLocalStorage URL param isn't false)`);
        if (this.urlParams.get("otLangLocalStorage") === 'false') return;
        localStorage.setItem(this.LOCAL_STORAGE_KEY, true);
    }
};

// Attach to window so run() method can be called from test suite
window.otLanguageAlert = otLanguageAlert;
otLanguageAlert.init();